/**
 * @module features/display-module/components/DdmSkeleton
 * @description Geometry-matched skeleton loader (`CLS = 0.00`) for the Display Module
 * workspace across Mobile (`<640px` card stack) and Tablet/Desktop (`>=640px` table) (`T033`).
 */

import * as React from "react";
import { Skeleton } from "@/components/ui/skeleton";

interface DdmSkeletonProps {
  rows?: number;
}

export const DdmSkeleton: React.FC<DdmSkeletonProps> = React.memo(
  ({ rows = 5 }) => {
    const items = React.useMemo(
      () => Array.from({ length: rows }, (_, idx) => idx),
      [rows]
    );

    return (
      <div
        role="status"
        aria-label="Loading display artifacts"
        className="w-full space-y-4"
      >
        {/* Desktop & Tablet Table Skeleton */}
        <div className="hidden sm:block rounded-2xl border border-border/80 dark:border-zinc-800/80 bg-card dark:bg-zinc-950/80 overflow-hidden shadow-xs">
          <div className="grid grid-cols-12 gap-4 px-5 py-3.5 border-b border-border/60 dark:border-zinc-800/70 bg-muted/30 dark:bg-zinc-900/40">
            <Skeleton className="h-3.5 w-24 col-span-3" />
            <Skeleton className="h-3.5 w-20 col-span-2" />
            <Skeleton className="h-3.5 w-24 col-span-2" />
            <Skeleton className="h-3.5 w-24 col-span-3" />
            <Skeleton className="h-3.5 w-16 col-span-2 justify-self-end" />
          </div>
          <div className="divide-y divide-border/50 dark:divide-zinc-800/60">
            {items.map((idx) => (
              <div
                key={idx}
                className="grid grid-cols-12 gap-4 items-center px-5 py-4 min-h-[72px]"
              >
                <div className="col-span-3 space-y-2">
                  <Skeleton className="h-4 w-36 rounded-md" />
                  <Skeleton className="h-3 w-48 rounded-md" />
                </div>
                <div className="col-span-2 space-y-1.5">
                  <Skeleton className="h-5 w-24 rounded-full" />
                  <Skeleton className="h-3 w-16 rounded-md" />
                </div>
                <div className="col-span-2 space-y-1.5">
                  <Skeleton className="h-4 w-28 rounded-md" />
                  <Skeleton className="h-3 w-20 rounded-md" />
                </div>
                <div className="col-span-3 space-y-1.5">
                  <Skeleton className="h-5 w-32 rounded-full" />
                  <Skeleton className="h-3 w-36 rounded-md" />
                </div>
                <div className="col-span-2 flex items-center justify-end gap-2">
                  <Skeleton className="h-8 w-8 rounded-lg" />
                  <Skeleton className="h-8 w-8 rounded-lg" />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Mobile Stacked Cards Skeleton (<640px) */}
        <div className="sm:hidden space-y-3">
          {items.map((idx) => (
            <div
              key={idx}
              className="rounded-2xl border border-border/80 dark:border-zinc-800/80 bg-card dark:bg-zinc-950/90 p-4 space-y-3 min-h-[148px] shadow-xs"
            >
              <div className="flex items-start justify-between gap-2">
                <div className="space-y-1.5 flex-1">
                  <Skeleton className="h-4 w-40 rounded-md" />
                  <Skeleton className="h-3 w-28 rounded-md" />
                </div>
                <Skeleton className="h-6 w-24 rounded-full" />
              </div>
              <Skeleton className="h-3.5 w-full rounded-md" />
              <div className="flex items-center justify-between pt-2 border-t border-border/50 dark:border-zinc-800/60">
                <Skeleton className="h-5 w-28 rounded-full" />
                <div className="flex items-center gap-2">
                  <Skeleton className="h-9 w-9 rounded-xl" />
                  <Skeleton className="h-9 w-9 rounded-xl" />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }
);

DdmSkeleton.displayName = "DdmSkeleton";

